import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { ICustomerPermissionDTO } from 'data/dtos/operation/i-customer-permission-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CustomerPermissionSchema, { ICustomerPermissionFormData } from 'schema/operation/customer-permission'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}
const CustomerPermissionForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [customersA, setCustomersA] = useState([])
  const [customersB, setCustomersB] = useState([])
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<ICustomerPermissionFormData>({
    resolver: ajvResolver(CustomerPermissionSchema),
    defaultValues: {
      keyPermission: '',
      sellerId: '',
      buyerId: '',
      isPermitted: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/customer-permissions/${id}`)
        .then((response) => {
          const { data } = response.data

          const customerPermissionResult = {
            keyPermission: data.keyPermission,
            sellerId: data.sellerId.id,
            buyerId: data.buyerId.id,
            isPermitted: data.isPermitted,
          }

          return customerPermissionResult
        })
        .then((customerPermissionResult: ICustomerPermissionDTO) => {
          reset(customerPermissionResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit: SubmitHandler<ICustomerPermissionFormData> = useCallback(async (data) => {
    setIsLoading(true)

    const payLoad: ICustomerPermissionFormData = {
      id: '',
      keyPermission: data.keyPermission,
      sellerId: data.sellerId,
      buyerId: data.buyerId,
      isPermitted: data.isPermitted,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/customer-permissions`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/customer-permissions')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/customer-permissions', payLoad)
        .then(() => {
          reset()
          openAlert('Dado cadastrado com sucesso!', 'success')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Permissões de Clientes'
        subTitle='Dados coletados sobre as permissões entre seller e buyer.'
      />

      <Form.Body>
        <Form.Input
          label='Chave da Permissão'
          error={!!errors.keyPermission}
          errorMessage={errors?.keyPermission?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='keyPermission'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.Select
          label='Seller'
          error={!!errors.sellerId}
          errorMessage={errors?.sellerId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='sellerId'
          rules={{
            onChange: (e) => {
              setValue('sellerId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().sellerId}`}
          options={customersA}
        />

        <Form.Select
          label='Buyer'
          error={!!errors.buyerId}
          errorMessage={errors?.buyerId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='buyerId'
          rules={{
            onChange: (e) => {
              setValue('buyerId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().buyerId}`}
          options={customersB}
        />

        <Form.CheckBox
          label='É permitido'
          sizing={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
          name='isPermitted'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default CustomerPermissionForm
